import React from "react";

export function useStl(url) {
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => (mountedRef.current = false);
  });

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [stlData, setStlData] = React.useState(null);

  React.useEffect(() => {
    setLoading(true);
    loadStl(url)
      .then((data) => {
        if (!mountedRef.current) return;
        setStlData(data);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        setError(err);
        setLoading(false);
      });
  }, [url]);

  return { loading, error, stlData };
}

export async function loadStl(url) {
  // see https://en.wikipedia.org/wiki/STL_%28file_format%29#Binary_STL
  const STL_HEADER_SIZE = 80;
  const UINT16_SIZE = 16 / 8;
  const UINT32_SIZE = 32 / 8;
  const FLOAT32_SIZE = 32 / 8;
  const VEC3_SIZE = FLOAT32_SIZE * 3;

  const FACE_SIZE =
    VEC3_SIZE + // Normal
    VEC3_SIZE * 3 + // Position
    UINT16_SIZE; // Attribute byte count

  const buffer = await fetch(url).then((res) => {
    if (res.status !== 200) throw new Error(res.statusText);
    return res.arrayBuffer();
  });
  const dataView = new DataView(buffer);

  const facesCount = dataView.getUint32(80, true /* little endian */);
  const vertexCount = facesCount * 3;

  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const boundingBox = {
    min: [Infinity, Infinity, Infinity],
    max: [-Infinity, -Infinity, -Infinity],
  };

  // loop faces
  for (let i = 0; i < facesCount; i++) {
    const faceStart = STL_HEADER_SIZE + UINT32_SIZE + i * FACE_SIZE;

    const normal = [
      dataView.getFloat32(faceStart, true),
      dataView.getFloat32(faceStart + 4, true),
      dataView.getFloat32(faceStart + 8, true),
    ];

    // loop verts
    for (let j = 0; j < 3; j++) {
      const vertexStart =
        faceStart +
        VEC3_SIZE + // Normal
        j * VEC3_SIZE;

      const index = i * 3 * 3 + j * 3;

      const [x, y, z] = [
        dataView.getFloat32(vertexStart, true),
        dataView.getFloat32(vertexStart + 4, true),
        dataView.getFloat32(vertexStart + 8, true),
      ];

      positions[index] = x;
      positions[index + 1] = y;
      positions[index + 2] = z;

      normals[index] = normal[0];
      normals[index + 1] = normal[1];
      normals[index + 2] = normal[2];

      boundingBox.min[0] = Math.min(boundingBox.min[0], x);
      boundingBox.min[1] = Math.min(boundingBox.min[1], y);
      boundingBox.min[2] = Math.min(boundingBox.min[2], z);

      boundingBox.max[0] = Math.max(boundingBox.max[0], x);
      boundingBox.max[1] = Math.max(boundingBox.max[1], y);
      boundingBox.max[2] = Math.max(boundingBox.max[2], z);
    }
  }

  const center = [
    boundingBox.min[0] + (boundingBox.max[0] - boundingBox.min[0]) / 2,
    boundingBox.min[1] + (boundingBox.max[1] - boundingBox.min[1]) / 2,
    boundingBox.min[2] + (boundingBox.max[2] - boundingBox.min[2]) / 2,
  ];

  return {
    vertexCount,
    positions,
    normals,
    boundingBox,
    center,
  };
}
